// bootSnapshot — scope-keyed warm-start boot cache (tickets/workspace.md
// "boot: scope-keyed warm-start snapshot"; PAINT half: tickets/opus.md
// "boot: the scope-keyed warm-start snapshot's PAINT half was never built").
//
// STATUS (2026-08-09): BOTH halves are wired.
//   Storage: write on every settled boot, purge on every workspace switch —
//   unchanged since 2026-08-08, see `writeBootSnapshot`/`purgeBootSnapshot`
//   below and their call sites in `loadPrivateData.js`.
//   Paint: `resolveWarmBootSnapshot()` (bottom of this file) is the ONE
//   integration point — workspace/index.js calls it before invoking
//   `bootShell({ ..., warmSnapshot })`. Nika granted permission 2026-08-09 to
//   edit `packages/shared/functions/bootShell.js` for this specific change
//   (see that file's own comment at the warm-start block for the accepted
//   conditions) — this module still does 100% of the "is this snapshot
//   actually safe to paint for THIS URL" judgment; bootShell only trusts an
//   already-validated row, never touches localStorage/the URL to decide.
//
// WHAT THIS BUYS: a repeat boot into a warm tenant paints identity chrome
// (org switcher, workspace name, member roster) in the SAME frame the shell
// mounts, instead of after the `/core/boot` round trip resolves. Measured
// 2026-08-08 (workspace@<see tickets/workspace.md>, pre-paint-half): COLD
// boot RTT ~1558ms / chrome painted ~2708ms; REPEAT boot RTT ~1213ms /
// chrome painted ~2907ms — i.e. even a REPEAT boot into the SAME tenant
// burned the full RTT before this half existed, because `bootShell` always
// awaited it before mounting anything. Re-measured after this change — see
// tickets/opus.md for the numbers.
//
// HOW THE PAINT HALF STAYS SAFE (the ticket's own bar: "a warm-start
// snapshot that paints stale or wrong-scope content is worse than no warm
// start"):
//   1. `userId` is decoded from the CURRENT `symbols_access_token` (never a
//      self-written "last known user" pointer) — a different signed-in user
//      in the same browser can never resolve another user's snapshot.
//   2. The lookup is keyed by the tab's OWN active-workspace pointer
//      (`activeWorkspacePointer.js` — sessionStorage per-tab when MWT is on),
//      exactly the same source `bootShell`'s live `resolveActiveWorkspace`
//      falls back to when the URL doesn't name a more specific scope.
//   3. `_snapshotMatchesUrlScope` cross-checks the URL against the snapshot's
//      OWN cached org/workspace identity using nothing but string compares —
//      no assumption, no guess. When the URL names an org/workspace that
//      disagrees with what's cached, this refuses (null) rather than paint
//      a plausible-looking wrong scope. See that function's own comments for
//      the exact precedence, which mirrors `resolveActiveWorkspace`'s live
//      one (URL org+handle wins; bare-org URL defers to the pointer already
//      being inside that org; MWT-off ignores the URL prefix entirely).
//   4. `readBootSnapshot` (unchanged) still refuses on schema-version drift,
//      staleness (7 days), or a missing/mismatched `orgId` embedded in the
//      payload.
//   5. bootShell's own reconcile (once the REAL `/core/boot` round trip
//      resolves, moments later) always wins over the cache — see that file's
//      warm-start block. A wrong or stale guess self-heals within one paint
//      cycle instead of persisting.
//
// SCOPE ISOLATION (the part that MUST be bulletproof even half-wired):
//   Storage key is `(userId × workspaceId)` — globally collision-free on its
//   own (Mongo ids), but the ticket's compound key is `(orgId × workspaceId)`,
//   so `orgId` is embedded in the payload and CROSS-CHECKED on read: a caller
//   that knows the expected orgId (post-boot verification, tests) passes it
//   and a mismatch is treated exactly like a missing snapshot. `userId` is
//   decoded from the CURRENT `symbols_access_token` (not a self-written "last
//   known user" pointer) so a different signed-in user in the same browser can never
//   read another user's snapshot even before any network round trip lands.
//   `schemaVersion` is stamped and checked — an older build's snapshot shape
//   is discarded, never coerced (mirrors the `_shape: 'full' | 'list'`
//   precedent in activeWorkspaceRecord.js: "agreement on identity is not
//   agreement on completeness").
//
// PURGE PATHS:
//   - Sign-out: the storage key is prefixed `symbols_boot_snapshot_v<N>__` —
//     `symbols_`-prefixed keys are ALREADY swept by the existing
//     `purgeAuthStorage()` (packages/shared/functions/clearAuthStorage.js,
//     used by both bootShell's pre-mount force-logout and the post-mount
//     switchers/logout.js). No shared-file edit needed to satisfy "signing
//     out purges it" — this rides the existing wipe path by construction.
//   - Workspace/org switch: `onWorkspaceChange`'s PHASE-1 wipe (this
//     package's `functions/loadPrivateData.js`) calls `purgeBootSnapshot` for
//     the OUTGOING (userId, workspaceId) before the new tenant's data lands —
//     belt-and-suspenders on top of the fact that a different tenant simply
//     reads a DIFFERENT storage key and could never paint the old one anyway.
//
// CONTENT: chrome-only. No tickets/documents/AI-thread/records data — this
// is explicitly NOT a general "cache the whole boot payload" store; it's the
// minimal set the ticket names for first-frame identity chrome.

// Only used by the PAINT-side resolver at the bottom of this file
// (`resolveWarmBootSnapshot`) — reuses the SAME URL-scheme parser and
// pointer/flag accessors `bootShell`'s own live resolution already goes
// through (`activeWorkspacePointer.js`'s `resolveActiveWorkspace`), so this
// module's "does the cache match the URL" precedence can never drift from
// what boot actually does with a live response.
// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import _wsShared from '@symbo-ls/workspace-shared'
// Deferred to call time — an eager module-scope destructure of the barrel's
// default crashes the shell boot inside the shared-barrel init cycle.
const shellGetWorkspaceRawSegments = (...args) => _wsShared.functions.getWorkspaceRawSegments(...args)
const shellGetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.getActiveWorkspacePointer(...args)
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
// v2 (P0 tenant isolation, tickets/fable.md 2026-08-14): workspace rows now
// cache `key` (`ws_<nanoid>`) so `_findByHandle` can resolve key-form URL
// handles — v1 rows couldn't, so a `/w/<org>/ws_<key>/…` URL read as an
// unrecognized ROUTE segment and a same-org snapshot for a DIFFERENT
// workspace could paint. The version bump discards every v1 row on read
// (schema drift is refused, never coerced); stale v1 localStorage keys ride
// purgeAuthStorage()'s existing `symbols_` sweep.
const SCHEMA_VERSION = 2
const KEY_PREFIX = `symbols_boot_snapshot_v${SCHEMA_VERSION}__`
// Chrome-relevant fields change rarely (org/workspace renames, roster
// additions) — a snapshot stays USEFUL long after it's technically stale,
// since the live boot always reconciles on arrival. Cap the age anyway so a
// months-old snapshot (e.g. a workspace the user hasn't opened in a long
// time) doesn't paint confidently wrong chrome for an extended first frame.
export const MAX_SNAPSHOT_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
// LRU cap — a browser that has visited many workspaces shouldn't grow this
// unboundedly. Keyed entries beyond the cap are evicted oldest-`ts` first.
export const MAX_SNAPSHOT_ENTRIES = 8

const _local = () => {
  try {
    return globalThis.localStorage || null
  } catch {
    return null
  }
}

// Monotonic write-order tiebreaker. `Date.now()` resolution (1ms) is coarse
// enough that a burst of writes (many workspaces warmed in quick succession —
// exactly what the LRU-cap path is defending against) can land the SAME `ts`,
// which would make "evict oldest" ambiguous among ties. A simple incrementing
// counter, unaffected by clock resolution, makes eviction order deterministic.
let _writeSeq = 0

// Pure, tolerant JWT payload decode — no verification (the token is already
// trusted; it's what authenticated this tab's own requests). Returns the
// Mongo user id the SDK's access token carries, or null on any malformed
// input. Never throws.
export const decodeAccessTokenUserId = (token) => {
  if (!token || typeof token !== 'string') return null
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const json =
      typeof atob === 'function'
        ? atob(b64 + pad)
        : Buffer.from(b64 + pad, 'base64').toString('utf8')
    const payload = JSON.parse(json)
    return String(payload?.sub || payload?._id || payload?.userId || '') || null
  } catch {
    return null
  }
}

export const _snapshotKey = (userId, workspaceId) =>
  `${KEY_PREFIX}${String(userId)}__${String(workspaceId)}`

// Every prefixed key currently in storage, newest-first by our own `ts`
// field (corrupt/foreign rows sort last and are treated as evictable).
const _allSnapshotKeys = (store) => {
  try {
    return Object.keys(store).filter((k) => k.startsWith(KEY_PREFIX))
  } catch {
    return []
  }
}

/**
 * Persist the CHROME-relevant slice of a fully-settled boot. No-ops (does
 * NOT write a partial/degraded snapshot) when any required identity field is
 * missing — a snapshot must never carry less identity than it claims to key
 * on.
 */
export const writeBootSnapshot = ({
  userId,
  orgId,
  workspaceId,
  currentUserEmail,
  currentUserUsername,
  org,
  orgs,
  workspace,
  workspaces,
  members,
  workspaceSkin
} = {}) => {
  const store = _local()
  if (!store) return false
  if (!userId || !orgId || !workspaceId) return false
  // THE SKIN (see recordBootSnapshotSkin below): a rewrite that does not know
  // it — the rule answers null while a configured module has not loaded —
  // keeps the skin this scope's row already carries.
  const skinOf = (v) => (v === 'ambient' || v === 'solid' ? v : null)
  let keptSkin = null
  if (!skinOf(workspaceSkin)) {
    try {
      const prev = JSON.parse(store.getItem(_snapshotKey(userId, workspaceId)) || 'null')
      keptSkin = prev && String(prev.userId) === String(userId) ? skinOf(prev.workspaceSkin) : null
    } catch {
      keptSkin = null
    }
  }
  const payload = {
    v: SCHEMA_VERSION,
    ts: Date.now(),
    seq: ++_writeSeq,
    userId: String(userId),
    orgId: String(orgId),
    workspaceId: String(workspaceId),
    currentUserEmail: currentUserEmail || '',
    currentUserUsername: currentUserUsername || '',
    org: org ? { id: org.id, name: org.name, slug: org.slug, logo: org.logo } : null,
    orgs: Array.isArray(orgs) ? orgs.map((o) => ({ id: o.id, name: o.name, slug: o.slug })) : [],
    workspace: workspace
      ? {
          id: workspace.id,
          name: workspace.name,
          slug: workspace.slug,
          key: workspace.key,
          org_id: workspace.org_id
        }
      : null,
    // The workspace's OWN interface-language pair — the one input bootShell's
    // first paint needs from `settings`, and the only part of `settings` this
    // chrome-only cache carries. bootShell resolves the warm mount's language
    // from it with the same `resolveLang` precedence applyWorkspaceLangPolicy
    // uses, so a Georgian workspace no longer paints English on the splash and
    // Georgian once the full record lands (measured on my.symbols.app,
    // 2026-09-23). Written only when the record carries a `settings` bag (the
    // caller writes on a settled boot, where the record is full); `null`
    // otherwise, which first paint reads as "no workspace signal" — the
    // behaviour before this field existed. Additive: an older v2 row simply
    // lacks it, so no schema bump.
    workspaceLanguage:
      workspace && workspace.settings && typeof workspace.settings === 'object'
        ? {
            language: workspace.settings.language || null,
            languagePolicy: workspace.settings.languagePolicy || null
          }
        : null,
    // The workspace's OWN default theme — `settings.theme = { mode }`, 'light' |
    // 'dark' or null — the other `settings` input first paint needs. The warm
    // mount seeds it (shared bootResolve.js 5b, below the user's own choice),
    // so a dark-by-default workspace (GreenIG, Nika 2026-10-18) paints dark
    // from its first frame; the cold mount reads the full record itself.
    // Same rule as workspaceLanguage: written when the record carries
    // `settings`, null otherwise. Additive: an older row lacks it.
    workspaceTheme: (() => {
      const settings =
        workspace && workspace.settings && typeof workspace.settings === 'object'
          ? workspace.settings
          : null
      const theme = settings ? settings.theme : null
      const mode = theme && typeof theme === 'object' ? theme.mode : theme
      return mode === 'light' || mode === 'dark' ? mode : null
    })(),
    // The skin the shell stamps for this scope — `ambient` | `solid`, or null
    // when not known yet (DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1).
    // A warm boot mounts from this row before /core/boot answers; the entry
    // (index.js, applyWarmBootSkin) stamps <html data-skin> from it before the
    // first paint, so the header pills and the composer are drawn in the
    // right skin from the first frame instead of changing when the record
    // lands. Additive, like workspaceLanguage: an older v2 row lacks it.
    workspaceSkin: skinOf(workspaceSkin) || keptSkin,
    workspaces: Array.isArray(workspaces)
      ? workspaces.map((w) => ({
          id: w.id,
          name: w.name,
          slug: w.slug,
          key: w.key,
          org_id: w.org_id,
          type: w.type
        }))
      : [],
    // Roster trimmed to what chrome (avatars, name) actually paints — not
    // the full HR-shaped `_person` row (bio/pronouns/social links/dates).
    members: Array.isArray(members)
      ? members.slice(0, 200).map((m) => ({
          userId: m.userId,
          name: m.name,
          avatar: m.avatar,
          email: m.email
        }))
      : []
  }
  try {
    store.setItem(_snapshotKey(userId, workspaceId), JSON.stringify(payload))
  } catch {
    return false
  }
  // LRU-evict beyond the cap so a browser that has warmed many tenants
  // doesn't grow this store unboundedly.
  try {
    const keys = _allSnapshotKeys(store)
    if (keys.length > MAX_SNAPSHOT_ENTRIES) {
      const withTs = keys
        .map((k) => {
          try {
            const row = JSON.parse(store.getItem(k) || 'null')
            return { k, ts: row?.ts || 0, seq: row?.seq || 0 }
          } catch {
            return { k, ts: 0, seq: 0 }
          }
        })
        .sort((a, b) => b.ts - a.ts || b.seq - a.seq)
      for (const { k } of withTs.slice(MAX_SNAPSHOT_ENTRIES)) {
        try {
          store.removeItem(k)
        } catch {}
      }
    }
  } catch {}
  return true
}

/**
 * Read + validate a snapshot for the exact (userId, workspaceId) scope.
 * Returns null on ANY of: absent, unparseable, wrong schema version, expired,
 * userId mismatch, workspaceId mismatch, or (when `orgId` is passed) orgId
 * mismatch. Never throws, never returns a partial/best-effort shape — a
 * caller either gets a fully-validated snapshot for the EXACT scope asked
 * for, or null.
 */
export const readBootSnapshot = ({ userId, workspaceId, orgId, maxAgeMs } = {}) => {
  const store = _local()
  if (!store || !userId || !workspaceId) return null
  let raw
  try {
    raw = store.getItem(_snapshotKey(userId, workspaceId))
  } catch {
    return null
  }
  if (!raw) return null
  let row
  try {
    row = JSON.parse(raw)
  } catch {
    return null
  }
  if (!row || row.v !== SCHEMA_VERSION) return null
  if (String(row.userId) !== String(userId)) return null
  if (String(row.workspaceId) !== String(workspaceId)) return null
  if (!row.orgId) return null
  if (orgId != null && String(row.orgId) !== String(orgId)) return null
  const age = Date.now() - (row.ts || 0)
  if (!(age >= 0) || age > (maxAgeMs ?? MAX_SNAPSHOT_AGE_MS)) return null
  return row
}

// A SKIN CHANGE after the settled boot (the admin picked the other skin, a
// module's own ground loaded) — patch this scope's row so the NEXT warm boot
// stamps the skin the shell settled on. Only an existing row for the exact
// (userId × workspaceId) key is touched, and only its skin: identity, chrome
// and age (`ts`) stay as they are, so a skin write never extends a snapshot's
// life. No row → nothing is created (a snapshot never carries less identity
// than it keys on). Returns whether the row was patched. Never throws.
export const recordBootSnapshotSkin = ({ userId, workspaceId, skin } = {}) => {
  const store = _local()
  if (!store || !userId || !workspaceId) return false
  if (skin !== 'ambient' && skin !== 'solid') return false
  const key = _snapshotKey(userId, workspaceId)
  let row
  try {
    row = JSON.parse(store.getItem(key) || 'null')
  } catch {
    return false
  }
  if (!row || row.v !== SCHEMA_VERSION) return false
  if (String(row.userId) !== String(userId)) return false
  if (String(row.workspaceId) !== String(workspaceId)) return false
  if (row.workspaceSkin === skin) return true
  try {
    store.setItem(key, JSON.stringify({ ...row, workspaceSkin: skin }))
    return true
  } catch {
    return false
  }
}

// The skin a (validated) warm snapshot row carries, or null. Pure.
export const warmBootSkin = (snap) => {
  const skin = snap && typeof snap === 'object' ? snap.workspaceSkin : null
  return skin === 'ambient' || skin === 'solid' ? skin : null
}

// THE STAMP BEFORE FIRST PAINT. The entry calls this with the row
// resolveWarmBootSnapshot validated for THIS tab and URL — the same attribute
// app.js _syncShellSkin keeps in step once the live record lands (where the
// rule agrees, it finds the attribute already right and changes nothing).
// Returns the skin it stamped, or null.
export const applyWarmBootSkin = (snap, doc = typeof document !== 'undefined' ? document : null) => {
  const skin = warmBootSkin(snap)
  const html = doc && doc.documentElement
  if (!skin || !html || typeof html.setAttribute !== 'function') return null
  if (html.getAttribute('data-skin') !== skin) html.setAttribute('data-skin', skin)
  return skin
}

// Purge one (userId, workspaceId) entry — called on workspace/org switch's
// PHASE-1 wipe for the OUTGOING scope (defense-in-depth; a different scope's
// key can never be read for the new tenant regardless).
export const purgeBootSnapshot = ({ userId, workspaceId } = {}) => {
  const store = _local()
  if (!store || !userId || !workspaceId) return
  try {
    store.removeItem(_snapshotKey(userId, workspaceId))
  } catch {}
}

// Purge every snapshot regardless of scope. Not on any hot path — the
// `symbols_` prefix already rides purgeAuthStorage()'s sweep on sign-out.
// Exposed for tests + any future explicit "clear local cache" debug action.
export const purgeAllBootSnapshots = () => {
  const store = _local()
  if (!store) return
  for (const k of _allSnapshotKeys(store)) {
    try {
      store.removeItem(k)
    } catch {}
  }
}

// ── PAINT-SIDE RESOLVER (tickets/opus.md — the PAINT half) ─────────────────
//
// The token bootShell itself checks for the SDK auth gate
// (`_hasSdkToken` there) — read locally here too so this module never
// depends on bootShell for identity resolution.
const _rawAccessToken = () => {
  const store = _local()
  if (!store) return null
  try {
    return (
      store.getItem('symbols_access_token') || store.getItem('symbols_bridge_access_token') || null
    )
  } catch {
    return null
  }
}

// Does `handle` name ANY workspace in `list` (the cached org-scoped roster)?
// Same precedence as `findWorkspaceInOrg` (activeWorkspacePointer.js):
// slug → key → id. `key` joined the cache at SCHEMA_VERSION 2 (P0 tenant
// isolation) — without it a key-form URL handle (`ws_<nanoid>`, the ONLY
// handle a slug-less workspace has) read as an unrecognized ROUTE segment,
// so a same-org snapshot keyed to a DIFFERENT workspace slipped past the
// scope match and painted for that URL.
const _findByHandle = (list, handle) => {
  if (!Array.isArray(list) || !handle) return null
  const want = String(handle).toLowerCase()
  const eq = (v) => String(v ?? '').toLowerCase() === want
  return list.find((w) => w && (eq(w.slug) || eq(w.key) || eq(w.id))) || null
}

/**
 * Pure. Does a cached snapshot belong to what `pathname` is asking for?
 *
 * Mirrors `activeWorkspacePointer.js`'s `resolveActiveWorkspace` precedence
 * EXACTLY, because that is the function that decides the REAL scope once
 * the live boot resolves — any drift between the two would mean this
 * resolver either refuses a snapshot the real boot would have used (a
 * missed win, safe but wasteful) or, far worse, accepts one the real boot
 * would NOT have used (a wrong-scope paint — the thing this whole ticket
 * exists to prevent). So every branch below is justified by name against
 * the matching branch in that function:
 *
 *   - MWT off, or the URL carries no `/w/<org>` prefix at all:
 *     `resolveActiveWorkspace` ignores the URL entirely and resolves purely
 *     off the tab's pointer. This snapshot IS keyed by that exact pointer
 *     (see `resolveWarmBootSnapshot` below) — always a match.
 *   - `/w/<org>` present, org differs from the snapshot's cached org: never
 *     a match — the live resolver would scope to a DIFFERENT org's list,
 *     which this snapshot has no data for.
 *   - `/w/<org>` present, org matches, NO second segment: `resolveActiveWorkspace`'s
 *     own bare-org branch checks "does the tab's pointer already resolve to a
 *     workspace INSIDE this org" BEFORE falling back to the org's HQ — and
 *     this snapshot is keyed by that identical pointer, so an org-slug match
 *     alone already proves the pointer is inside this org. No HQ check
 *     needed (an earlier draft of this function required one — that was
 *     wrong; it refused the pointer's own workspace on a bare-org URL
 *     whenever the pointer wasn't parked on the HQ, which is exactly the
 *     case the live resolver does NOT fall back to HQ for).
 *   - `/w/<org>/<seg2>` present, `seg2` matches a DIFFERENT workspace in the
 *     cached org roster (`snap.workspaces`) than the one this snapshot is
 *     for: never a match — `resolveActiveWorkspace` resolves `seg2` purely
 *     against the org's live workspace list (`_findInOrg`, NOT the pointer),
 *     and it would pick that OTHER workspace, which this snapshot has no
 *     cached chrome for.
 *   - `/w/<org>/<seg2>` present, `seg2` matches THIS snapshot's own cached
 *     workspace: match.
 *   - `/w/<org>/<seg2>` present, `seg2` matches NO workspace in the cached
 *     org roster at all: this is `getWorkspaceRawSegments`'s raw, UNGATED
 *     guess — `seg2` may just be the first segment of an ordinary intranet
 *     ROUTE (`/w/acme/tasks`), not a workspace handle at all. Confirmed by
 *     reading `resolveActiveWorkspace` itself: when its own `_findInOrg`
 *     lookup for `seg2` comes back empty, it does NOT refuse — it falls
 *     through to the exact same "pointer already inside this org, else HQ"
 *     branch the bare-org case uses. Mirror that fallthrough (match), rather
 *     than the far more common case (multi-segment intranet routes on a
 *     `/w/<org>/…` URL) silently missing the warm-start win it's entitled to.
 *   - `/w/<org>/default`, and NO cached workspace is slugged `default`: the
 *     live resolver picks the org's MAIN workspace (the server's meaning of
 *     `default`; Nika 2026-10-18), never the pointer. Match only when this
 *     snapshot is that workspace by the same rule — a snapshot keyed by a
 *     pointer parked in a sibling would paint the sibling's chrome.
 */
export const _snapshotMatchesUrlScope = (
  snap,
  { orgSlug, seg2 } = {},
  { mwtEnabled = true } = {}
) => {
  if (!snap) return false
  if (!mwtEnabled || !orgSlug) return true
  const snapOrgSlug = String(snap.org?.slug || '').toLowerCase()
  if (!snapOrgSlug || snapOrgSlug !== String(orgSlug).toLowerCase()) return false
  if (!seg2) return true
  const hit = _findByHandle(snap.workspaces, seg2)
  const cachedId = String(snap.workspace?.id || '')
  if (!hit && String(seg2).toLowerCase() === 'default') {
    // `default` that no cached workspace claims: the live resolver picks the
    // org's MAIN workspace (type hq → shared → first of the org), never the
    // pointer this snapshot is keyed by. Match only when this snapshot IS
    // that workspace. The live tie-breaks (first `shared`, first row) follow
    // the LIVE list's order, which this cached roster need not share, so only
    // a unique row at its tier counts; anything else proves nothing and
    // refuses, like an empty roster.
    const orgId = String(snap.orgId || snap.workspace?.org_id || '')
    const mine = (Array.isArray(snap.workspaces) ? snap.workspaces : []).filter(
      (w) => w && String(w.org_id ?? w.organization ?? '') === orgId
    )
    const only = (rows) => (rows.length === 1 ? rows[0] : null)
    const hqs = mine.filter((w) => w.type === 'hq')
    const shared = mine.filter((w) => w.type === 'shared')
    const main = hqs.length ? only(hqs) : shared.length ? only(shared) : only(mine)
    return !!cachedId && !!main && String(main.id || '') === cachedId
  }
  if (!hit) return true // unrecognized segment — treated as a ROUTE, mirrors the live fallthrough
  return !!cachedId && String(hit.id || '') === cachedId
}

/**
 * THE integration point a boot caller uses. Resolves a scope-correct warm
 * snapshot for the CURRENT tab/URL, or `null` when none exists or none can
 * be proven to match — never a guess. Impure (localStorage + URL); never
 * throws. Callers pass the raw returned row straight through as
 * `bootShell({ warmSnapshot })` — bootShell trusts it as already-validated
 * and does no scope re-derivation of its own (see that file's warm-start
 * block).
 *
 * `pathname` defaults to the live `window.location.pathname` — overridable
 * for tests and for a caller that already has the path in hand.
 */
// The signed-in user's org list from the warm snapshot (perf/shell-boot,
// review Delta 2 N8): the boot module prefetch gate resolves a `/w/<org>` slug
// against it before /core/boot's own lists are on the root. Keyed by the
// token's user + the workspace (readBootSnapshot), and refused when older than
// `maxAgeMs` (one day here — an org list is small and stale only rarely, but
// the shared readCachedOrgs() row it replaces had no user key and no age).
// A stale or missing list only skips a prefetch; the server still gates every
// module read by workspace id.
export const readSessionBootOrgs = ({ workspaceId, maxAgeMs = 24 * 60 * 60 * 1000 } = {}) => {
  try {
    const userId = decodeAccessTokenUserId(_rawAccessToken())
    if (!userId || !workspaceId) return null
    const row = readBootSnapshot({ userId, workspaceId, maxAgeMs })
    return row && Array.isArray(row.orgs) && row.orgs.length ? row.orgs : null
  } catch {
    return null
  }
}

export const resolveWarmBootSnapshot = ({ pathname } = {}) => {
  try {
    const userId = decodeAccessTokenUserId(_rawAccessToken())
    if (!userId) return null
    const workspaceId = shellGetActiveWorkspacePointer()
    if (!workspaceId) return null
    const snap = readBootSnapshot({ userId, workspaceId })
    if (!snap) return null
    const path =
      pathname != null
        ? pathname
        : (typeof window !== 'undefined' && window.location && window.location.pathname) || ''
    const rawSegs = shellGetWorkspaceRawSegments(path)
    if (!_snapshotMatchesUrlScope(snap, rawSegs, { mwtEnabled: shellIsMwtEnabled() })) return null
    return snap
  } catch (err) {
    console.warn('[bootSnapshot] resolveWarmBootSnapshot failed:', err?.message || err)
    return null
  }
}
